import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Phone, CheckCircle2, Flame, Star, BookOpen } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { useLang } from "../i18n/LanguageContext";
import { SITE } from "../i18n/translations";
import { useBooking } from "../context/BookingContext";

const EXPERTISE = [
  { icon: Flame, title: "Tarapith Tantra & Maa Tara Sadhana", desc: "Worship, sadhana and remedies rooted in the authentic Shakta-Tantric tradition of Tarapith." },
  { icon: Star, title: "Vedic Astrology (Jyotish)", desc: "Kundali analysis and traditional remedial guidance offered responsibly and transparently." },
  { icon: BookOpen, title: "Puja, Havan & Devotional Rituals", desc: "Guidance and arrangement of traditional puja and havan performed with care and authenticity." },
];

export default function About() {
  const { t } = useLang();
  const { openBooking } = useBooking();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name: SITE.practitioner,
      jobTitle: "Tantrik Sadhak & Vedic Astrologer",
      url: `${SITE.domain}/about`,
      telephone: `+${SITE.whatsapp}`,
      knowsAbout: ["Tarapith Tantra", "Maa Tara", "Dashamahavidya", "Shakta Tantra", "Vedic Astrology"],
      worksFor: { "@type": "Organization", name: SITE.name, url: SITE.domain },
    },
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo
        title={`About ${SITE.practitioner} — Tantrik Sadhak & Vedic Astrologer`}
        description={`Learn about ${SITE.practitioner}, the practitioner behind TarapithTantrik.com — his role, expertise in Tarapith Tantra, Maa Tara worship and Vedic astrology, and how to connect.`}
        path="/about"
        keywords={["Shivam Chatterjee", "Tarapith tantrik", "Vedic astrologer", "Maa Tara puja"]}
        jsonLd={jsonLd}
      />
      <Breadcrumbs items={[{ label: t("nav_about") }]} />

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start mb-14">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="glass glass-reflect rounded-3xl p-8 text-center">
          <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-tara-gold to-tara-vermilion mb-5 flame-flicker">
            <Flame className="h-12 w-12 text-white" />
          </div>
          <h2 className="font-serif text-2xl font-bold gold-text mb-1">{SITE.practitioner}</h2>
          <p className="text-sm text-tara-muted mb-5">Tantrik Sadhak & Vedic Astrologer</p>
          <a href={`tel:+${SITE.whatsapp}`} className="flex items-center justify-center gap-2 text-sm text-tara-body mb-2" data-testid="about-phone">
            <Phone className="h-4 w-4 text-tara-gold" /> {SITE.phoneDisplay}
          </a>
          <button onClick={() => openBooking()} className="aura-pulse w-full mt-4 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-tara-gold to-tara-vermilion px-6 py-3 font-serif font-semibold text-white hover:brightness-110 transition" data-testid="about-connect-btn">
            <MessageCircle className="h-5 w-5" /> {t("cta_speak")}
          </button>
        </motion.div>

        <div>
          <span className="text-xs font-serif tracking-[0.25em] uppercase text-tara-gold/90">About the Practitioner</span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-tara-ink mt-2 mb-5">{SITE.practitioner}</h1>
          <div className="prose-tara">
            <p>
              {SITE.practitioner} is the practitioner behind TarapithTantrik.com — a platform dedicated to the sacred
              tradition of Tarapith Tantra and the worship of Maa Tara. His work brings together the living Shakta-Tantric
              heritage of Tarapith with the classical science of Vedic astrology (Jyotish).
            </p>
            <p>
              Through TarapithTantrik.com he offers respectful spiritual guidance, arranges traditional puja and havan, and
              provides astrology consultation for those seeking clarity during difficult times. His approach is grounded in
              authenticity and honesty: tradition is presented faithfully, and no impossible promises are made.
            </p>
            <p>
              The platform's mission is to make deep, well-organised knowledge about Tarapith, Maa Tara and Tantra accessible
              worldwide — and to make it easy for those who wish to connect for genuine guidance to do so with trust.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-3 mb-14">
        {EXPERTISE.map((e, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.08 }} className="glass rounded-2xl p-6">
            <e.icon className="h-8 w-8 text-tara-gold mb-3" />
            <h3 className="font-display text-xl font-semibold text-tara-ink mb-2">{e.title}</h3>
            <p className="text-sm text-tara-muted leading-relaxed">{e.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="glass-strong rounded-2xl p-8">
        <h2 className="font-serif text-2xl font-semibold text-tara-ink mb-4">A commitment to trust (E-E-A-T)</h2>
        <ul className="space-y-3">
          {[
            "Clear distinction between verified history, traditional belief and folklore.",
            "Content authored and reviewed with attention to factual accuracy.",
            "Responsible language — no guaranteed supernatural outcomes.",
            "Spiritual services do not replace medical, mental-health, legal or financial professionals.",
          ].map((x, i) => (
            <li key={i} className="flex items-start gap-3 text-tara-body">
              <CheckCircle2 className="h-5 w-5 text-tara-gold shrink-0 mt-0.5" /> {x}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
